import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, fmtErr } from "../lib/api";
import { toast } from "sonner";
import { Sparkle, DownloadSimple, CheckCircle, WarningCircle, LinkedinLogo, CaretDown, CaretUp, Brain } from "@phosphor-icons/react";
import { RatingBadge } from "./RatingBadge";

export const ApolloMorePanel = ({ domain, company, titles, onCreditsUsed, ratings = {}, onRequestRate, defaultHealthcareOnly = false }) => {
  const [open, setOpen] = useState(false);
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(false);
  const [importing, setImporting] = useState(false);
  const [sel, setSel] = useState(new Set());
  const [searched, setSearched] = useState(false);
  const [error, setError] = useState("");
  const [hideNoEmail, setHideNoEmail] = useState(true);
  const [excluded, setExcluded] = useState(0);
  const [rateBusy, setRateBusy] = useState(false);
  const [hcOnly, setHcOnly] = useState(defaultHealthcareOnly);

  useEffect(() => { if (!searched) setHcOnly(defaultHealthcareOnly); }, [defaultHealthcareOnly, searched]);
  const [usedTitles, setUsedTitles] = useState("");

  const titlesKey = titles.join("|");
  const PER_PAGE = 100;
  const MAX_AUTO = 500;

  const fetchPage = (p, hc = hcOnly) => api.apolloSearch({ domain, company, titles, page: p, per_page: PER_PAGE, keywords: hc ? "healthcare" : "" });

  const searchAll = async (hc = hcOnly) => {
    setLoading(true); setError("");
    try {
      let p = 1, all = [], tot = 0, exc = 0;
      while (true) {
        const r = await fetchPage(p, hc);
        all = [...all, ...r.people]; tot = r.total_entries; exc += (r.excluded_count || 0);
        setRows([...all]); setTotal(tot); setExcluded(exc); setPage(p);
        if (p * PER_PAGE >= tot || (r.people.length === 0 && !(r.excluded_count > 0)) || all.length >= MAX_AUTO) break;
        p += 1;
      }
      setSearched(true);
      setUsedTitles(titles.join("|"));
      setSel(new Set());
      onCreditsUsed?.();
    } catch (err) {
      setError(fmtErr(err));
    } finally { setLoading(false); }
  };

  const loadMore = async () => {
    setLoading(true); setError("");
    try {
      const p = page + 1;
      const r = await fetchPage(p);
      setRows(prev => [...prev, ...r.people]);
      setTotal(r.total_entries);
      setExcluded(prev => prev + (r.excluded_count || 0));
      setPage(p);
      onCreditsUsed?.();
    } catch (err) {
      setError(fmtErr(err));
    } finally { setLoading(false); }
  };

  const expand = () => {
    const next = !open;
    setOpen(next);
    if (next && !searched && !loading) searchAll();
  };

  const eligible = rows.filter(r => !r.imported && !r.in_db && !r.likely_in_db && !r.hunter_has && r.has_email);
  const allSel = eligible.length > 0 && eligible.every(r => sel.has(r.id));
  const toggle = (id) => setSel(prev => { const n = new Set(prev); n.has(id) ? n.delete(id) : n.add(id); return n; });
  const noEmailCount = rows.filter(r => !r.has_email || r.likely_in_db || r.hunter_has).length;
  const visible = hideNoEmail ? rows.filter(r => r.has_email && !r.likely_in_db && !r.hunter_has) : rows;

  const doImport = async () => {
    const chosen = rows.filter(r => sel.has(r.id) && !r.imported);
    if (!chosen.length) return;
    const toReveal = chosen.filter(r => !r.revealed_email).length;
    const cachedN = chosen.length - toReveal;
    if (!window.confirm(`Reveal and import ${chosen.length} ${chosen.length === 1 ? "person" : "people"} from Apollo? ~${toReveal} Apollo credit${toReveal === 1 ? "" : "s"} will be used${cachedN ? ` (${cachedN} already revealed — free)` : ""}.`)) return;
    setImporting(true);
    try {
      const r = await api.apolloRevealImport({ company, domain, people: chosen.map(c => ({ id: c.id })) });
      const sk = r.skipped || {};
      toast.success(`Imported ${r.created} to Contacts (unverified) · ${r.credits_charged} Apollo credit${r.credits_charged === 1 ? "" : "s"} used${sk.already_in_db ? ` · ${sk.already_in_db} already in MediHub` : ""}${r.not_found ? ` · ${r.not_found} had no usable email at Apollo` : ""}`);
      const done = new Set(chosen.map(c => c.id));
      setRows(prev => prev.map(row => done.has(row.id) ? { ...row, imported: true } : row));
      setSel(new Set());
      onCreditsUsed?.();
    } catch (err) {
      toast.error(fmtErr(err));
    } finally { setImporting(false); }
  };

  const statusCell = (r) => {
    if (r.imported) return <span className="inline-flex items-center gap-1 text-emerald-700"><CheckCircle size={13} weight="fill" /> imported</span>;
    if (r.in_db) return <span className="inline-flex items-center gap-1 text-emerald-700"><CheckCircle size={13} weight="fill" /> in MediHub</span>;
    if (r.likely_in_db) return <span className="inline-flex items-center gap-1 text-amber-600" title="Someone with this first name and last initial at this domain is already in MediHub"><WarningCircle size={13} /> may already be in MediHub</span>;
    if (r.hunter_has) return <span className="text-zinc-400" title="Hunter also found this person — check the Hunter results above">Hunter found them too</span>;
    if (!r.has_email) return <span className="inline-flex items-center gap-1 text-amber-600"><WarningCircle size={13} /> no email at Apollo</span>;
    return <span className="text-violet-700 font-medium">new — Hunter missed them</span>;
  };

  return (
    <div className="rounded-lg border border-violet-200 bg-white overflow-hidden" data-testid="apollo-panel">
      <button type="button" onClick={expand} data-testid="apollo-find-more-btn"
        className="w-full px-3 py-2.5 flex items-center gap-2 text-left hover:bg-violet-50/60 transition-colors">
        <Sparkle size={16} weight="fill" className="text-violet-600" />
        <span className="text-sm font-medium text-zinc-900">Find more with Apollo</span>
        <span className="text-xs text-zinc-400">search Apollo's 270M-contact database for people at {domain} that Hunter missed{titles.length ? ` · titles: ${titles.join(", ")}` : ""}</span>
        {open ? <CaretUp size={14} className="ml-auto text-zinc-400" /> : <CaretDown size={14} className="ml-auto text-zinc-400" />}
      </button>

      {open && (
        <div className="border-t border-violet-100">
          {error && <div className="px-3 py-2 text-sm text-rose-700 bg-rose-50" data-testid="apollo-error">{error}</div>}
          {loading && rows.length === 0 && <div className="p-8 text-center text-sm text-zinc-500">Searching Apollo…</div>}
          {searched && !error && usedTitles !== titlesKey && (
            <div className="px-3 py-2 text-xs text-amber-700 bg-amber-50 border-b border-amber-100 flex items-center gap-2" data-testid="apollo-kw-stale">
              Keyword selection changed since this Apollo search.
              <button type="button" onClick={() => searchAll()} disabled={loading}
                data-testid="apollo-kw-refresh"
                className="px-2 py-0.5 rounded border border-amber-300 bg-white font-medium hover:border-amber-400 disabled:opacity-40">
                {loading ? "Searching…" : "Search Apollo again with current keywords"}
              </button>
            </div>
          )}
          {searched && !error && (
            <>
              <div className="px-3 py-2 text-sm text-zinc-600 border-b border-zinc-100" data-testid="apollo-summary">
                <span className="font-medium text-zinc-900">{Number(total).toLocaleString()}</span> people at {domain} in Apollo{titles.length ? " matching your keywords" : ""} — searching is ~free, you pay ~1 Apollo credit per email you reveal.
                {excluded > 0 && <span className="text-rose-500"> {excluded} hidden by your exclusion list.</span>}
                <label className="ml-2 inline-flex items-center gap-1 text-xs text-zinc-500 cursor-pointer align-middle" title="Only people whose Apollo profile mentions healthcare — useful at diversified companies">
                  <input type="checkbox" checked={hcOnly} data-testid="apollo-hc-toggle"
                    onChange={() => { const v = !hcOnly; setHcOnly(v); if (searched) searchAll(v); }} />
                  healthcare profiles only
                </label>
                {noEmailCount > 0 && (
                  <button type="button" onClick={() => setHideNoEmail(v => !v)} data-testid="apollo-toggle-no-email"
                    className="ml-2 text-xs text-zinc-400 underline hover:text-zinc-600">
                    {hideNoEmail ? `${noEmailCount} hidden (no email / already in MediHub or Hunter) — show` : `hide ${noEmailCount} (no email / already in MediHub or Hunter)`}
                  </button>
                )}
              </div>
              <table className="w-full text-sm">
                <thead className="text-xs text-zinc-500 border-b border-zinc-200"><tr>
                  <th className="px-3 py-2 w-8"><input type="checkbox" checked={allSel} data-testid="apollo-select-all"
                    onChange={() => setSel(allSel ? new Set() : new Set(eligible.map(r => r.id)))} /></th>
                  <th className="text-left px-3 py-2 font-medium">Name</th>
                  <th className="text-left px-3 py-2 font-medium">Job title</th>
                  <th className="text-left px-3 py-2 font-medium">Email</th>
                  <th className="text-left px-3 py-2 font-medium w-10">In</th>
                  <th className="text-left px-3 py-2 font-medium">Status</th>
                </tr></thead>
                <tbody>
                  {visible.map((r, i) => (
                    <tr key={r.id} className={`border-b border-zinc-100 ${r.imported || r.in_db ? "opacity-60" : ""}`} data-testid={`apollo-row-${i}`}>
                      <td className="px-3 py-2">
                        <input type="checkbox" disabled={r.imported || r.in_db || !r.has_email} checked={sel.has(r.id)} onChange={() => toggle(r.id)}
                          data-testid={`apollo-check-${i}`} />
                      </td>
                      <td className="px-3 py-2 font-medium text-zinc-900 whitespace-nowrap">{`${r.first_name} ${r.last_name}`.trim() || "—"}{ratings[r.id] && <span className="ml-1.5 align-middle inline-block"><RatingBadge v={ratings[r.id]} /></span>}</td>
                      <td className="px-3 py-2 text-zinc-600 max-w-[280px] truncate" title={r.title}>{r.title || "—"}</td>
                      <td className="px-3 py-2 font-mono text-[12.5px]">{r.revealed_email || <span className="text-zinc-400 font-sans text-xs">{r.has_email ? "hidden until reveal" : "—"}</span>}</td>
                      <td className="px-3 py-2">
                        {r.linkedin && <a href={r.linkedin} target="_blank" rel="noreferrer" className="text-zinc-400 hover:text-sky-600" title="Open LinkedIn"><LinkedinLogo size={15} /></a>}
                      </td>
                      <td className="px-3 py-2 text-xs">{statusCell(r)}</td>
                    </tr>
                  ))}
                  {visible.length === 0 && !loading && <tr><td colSpan={6} className="p-8 text-center text-sm text-zinc-500">{rows.length > 0 ? "Everyone found is hidden (no email at Apollo, already found by Hunter, or may already be in MediHub) — use the show link above to see them." : <>Apollo has nobody{titles.length ? " matching your keywords" : ""} at {domain}{titles.length ? " — try clearing some keywords and re-opening the panel" : ""}.</>}</td></tr>}
                </tbody>
              </table>
              <div className="flex flex-wrap items-center gap-3 px-3 py-2.5">
                {onRequestRate && rows.length > 0 && (
                  <button data-testid="apollo-rate-btn" disabled={rateBusy || loading}
                    onClick={async () => {
                      const people = visible.filter(r => r.title).map(r => ({ key: r.id, name: `${r.first_name} ${r.last_name}`.trim(), title: r.title }));
                      if (!people.length) { toast.error("No visible people with job titles to rate"); return; }
                      setRateBusy(true);
                      try { const rr = await onRequestRate(people); toast.success(`Rated ${rr.rated} against the Medicys ICP`); }
                      catch (e) { toast.error(fmtErr(e)); }
                      finally { setRateBusy(false); }
                    }}
                    className="h-9 px-4 rounded-md border border-indigo-200 bg-white text-sm font-medium text-indigo-700 hover:border-indigo-400 disabled:opacity-40 flex items-center gap-1.5">
                    <Brain size={14} /> {rateBusy ? "Rating…" : "Rate with AI (ICP)"}
                  </button>
                )}
                {page * PER_PAGE < total && (
                  <button onClick={loadMore} disabled={loading || importing} data-testid="apollo-load-more"
                    className="h-9 px-4 rounded-md border border-zinc-200 bg-white text-sm font-medium text-zinc-700 hover:border-zinc-300 disabled:opacity-40">
                    {loading ? "Loading…" : `Load 100 more (${Math.min(page * PER_PAGE, total).toLocaleString()} of ${Number(total).toLocaleString()} scanned)`}
                  </button>
                )}
                {sel.size > 0 && (
                  <button onClick={doImport} disabled={importing || loading} data-testid="apollo-import-btn"
                    className="ml-auto h-9 px-4 rounded-md bg-violet-700 text-white text-sm font-medium hover:bg-violet-800 disabled:opacity-40 flex items-center gap-1.5">
                    <DownloadSimple size={14} />{importing ? "Revealing…" : `Reveal & import ${sel.size} (≈${rows.filter(r => sel.has(r.id) && !r.revealed_email).length} Apollo credit${rows.filter(r => sel.has(r.id) && !r.revealed_email).length === 1 ? "" : "s"})`}
                  </button>
                )}
              </div>
              <p className="px-3 pb-3 text-xs text-zinc-400">Apollo imports arrive with source "apollo" and <span className="font-medium text-zinc-500">unverified</span> deliverability — run <Link to="/contacts" className="underline hover:text-zinc-600">Verify with Hunter in bulk</Link> before emailing them. Rows Hunter also found, or that look already present in MediHub, are left out of select-all — tick them individually if you're sure.</p>
            </>
          )}
        </div>
      )}
    </div>
  );
};
